import { type Cell, flexRender } from "@tanstack/react-table";

import { TableCell } from "@vega-core/components/shadcn/table";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { useOverflowObserver } from "@vega-core/hooks/useOverflowObserver";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { usePinnedColumns } from "../hooks/usePinnedColumns";

type TableCellProps<TData, TValue> = {
  cell: Cell<TData, TValue>;
  cellClassName?: string;
  dataTestId?: string;
  pinnedLeftColumns?: string[];
  isScrolled?: boolean;
  rowClassName?: string;
  cellByRowClassName?: string;
};

export function TableCellComponent<TData, TValue>({
  cell,
  cellClassName,
  dataTestId,
  pinnedLeftColumns = [],
  isScrolled = false,
  rowClassName,
  cellByRowClassName,
}: TableCellProps<TData, TValue>) {
  const [contentRef, isOverflowing] = useOverflowObserver<HTMLDivElement>({
    axis: "both",
    includeDescendants: true,
  });

  const { getPinnedColumnProps } = usePinnedColumns({
    pinnedLeftColumns,
    table: cell.getContext().table,
  });

  const {
    isPinned,
    isLastPinned,
    leftOffset,
    columnWidth,
    columnMaxSize,
    columnMinSize,
  } = getPinnedColumnProps(cell.column.id);

  const noTruncate = cell.column.columnDef.noTruncate ?? false;
  const addCellTooltip =
    (cell.column.columnDef.addCellTooltip ?? true) && !noTruncate;
  const showTooltip = addCellTooltip && isOverflowing;

  const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());

  const isMobileSelector = cell.column.columnDef.isMobileSelector ?? false;

  const align = cell.column.columnDef.align ?? "left";

  const contentElement = (
    <div
      className={cnTwMerge(
        "block w-full",
        noTruncate ? "whitespace-normal break-words" : "truncate",
        {
          "ml-auto text-right": align === "right",
          "mr-auto text-left": align === "left",
          "ml-auto mr-auto": isMobileSelector || align === "center",
          "!w-min !p-0 text-center": isMobileSelector,
          "text-center": align === "center" && !isMobileSelector,
        },
      )}
      ref={contentRef}
    >
      {cellContent}
    </div>
  );

  const finalContent = isLastPinned ? (
    <div className="relative h-full w-full contents">
      {contentElement}
      <div
        className={cnTwMerge(
          "absolute top-0 -right-2 bottom-0 w-2 bg-gradient-to-r from-background/component/20 to-transparent pointer-events-none defaultTransition",
          isScrolled ? "opacity-100" : "opacity-0 max-w-0",
        )}
      />
    </div>
  ) : (
    <div className={cnTwMerge("block w-full")}>{contentElement}</div>
  );

  const shouldStick = isPinned && isScrolled;

  const cellClickHandler = cell.column.columnDef.onClick
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        cell.column.columnDef.onClick!(cell.row.original);
      }
    : undefined;

  return (
    <TableCell
      className={cnTwMerge(
        "[&:has([role=checkbox])]:px-0 ",
        cellClassName,
        cell.column.columnDef.className,
        shouldStick
          ? "sticky z-10 bg-background/primary group-hover:bg-background/secondary group-data-[state=selected]:bg-background/secondary transition-colors duration-150"
          : "group-hover:bg-background/secondary group-data-[state=selected]:bg-background/secondary transition-colors duration-150",
        {
          "text-right": align === "right",
          "text-left": align === "left",
          "text-center": align === "center",
          "cursor-pointer": cell.column.columnDef.onClick,
        },
        rowClassName,
        cellByRowClassName,
      )}
      data-testid={dataTestId}
      key={cell.id}
      onClick={cellClickHandler}
      style={
        shouldStick
          ? {
              left: leftOffset,
              width: columnWidth,
              minWidth: columnMinSize,
              maxWidth: columnMaxSize,
            }
          : {
              width: cell.column.getSize(),
              minWidth: cell.column.columnDef.minSize,
              maxWidth: cell.column.columnDef.maxSize,
            }
      }
    >
      {showTooltip ? (
        <Tooltip content={contentElement}>
          <span>{finalContent}</span>
        </Tooltip>
      ) : (
        finalContent
      )}
    </TableCell>
  );
}
